import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  getMultiFactorResolver,
  isSignInWithEmailLink,
  multiFactor,
  signInWithEmailLink,
  signInWithPopup,
  signOut,
} from 'firebase/auth';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  TextField,
} from '@mui/material';
import { auth } from '../firebase';
import { callBackend } from '../utils/api';
import {
  createGoogleAuthProvider,
  createMicrosoftAuthProvider,
  describeAuthError,
} from '../utils/federatedAuth';
import MFASecondFactor from './MFASecondFactor';
import MFAEnrollment from './MFAEnrollment';
import AuthCardShell from './AuthCardShell';

const INVITATION_TOKEN_STORAGE_KEY = 'alphamizer.pending-invitation-token';

function tokenFromUrl(rawUrl) {
  try {
    const url = new URL(rawUrl);
    const direct = url.searchParams.get('invite');
    if (direct) return direct;
    const continueUrl = url.searchParams.get('continueUrl');
    if (!continueUrl) return '';
    return new URL(continueUrl).searchParams.get('invite') || '';
  } catch {
    return '';
  }
}

export function resolveInvitationToken(rawUrl = window.location.href) {
  const fromUrl = tokenFromUrl(rawUrl);
  if (fromUrl) {
    sessionStorage.setItem(INVITATION_TOKEN_STORAGE_KEY, fromUrl);
    return fromUrl;
  }
  return sessionStorage.getItem(INVITATION_TOKEN_STORAGE_KEY) || '';
}

function backendInvitationMessage(error) {
  switch (error?.code) {
    case 'INVITATION_EXPIRED':
      return 'This invitation has expired. Ask an administrator to resend it.';
    case 'INVITATION_EMAIL_UNVERIFIED':
      return 'The selected account does not have a verified email address.';
    case 'INVITATION_PROVIDER_UNSUPPORTED':
      return 'Use Google, Microsoft, or the secure email link to accept this invitation.';
    case 'INVITATION_IDENTITY_CONFLICT':
      return 'This invitation is already associated with a different account. Contact an administrator.';
    case 'INVITATION_NOT_AVAILABLE':
      return 'This invitation is invalid, already used, or does not match the selected account.';
    case 'MFA_REQUIRED':
      return 'Complete SMS multi-factor authentication before accepting this invitation.';
    default:
      return 'Alphamizer could not complete this invitation. Please try again.';
  }
}

export default function InviteAcceptance({ user, onAccepted }) {
  const [token] = useState(() => resolveInvitationToken());
  const [invitation, setInvitation] = useState(null);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [pendingMethod, setPendingMethod] = useState('');
  const [error, setError] = useState('');
  const [mfaResolver, setMfaResolver] = useState(null);
  const acceptanceUidRef = useRef('');
  // An account already signed in when the link opened may not be the invited
  // one (e.g. the admin who sent it). Use it only after an explicit choice;
  // signing in on this page replaces it with the invited account.
  const initialUidRef = useRef(user?.uid || '');
  const [confirmedUid, setConfirmedUid] = useState('');
  const sessionConfirmed = Boolean(user?.uid)
    && (user.uid !== initialUidRef.current || user.uid === confirmedUid);
  const emailLinkAvailable = isSignInWithEmailLink(auth, window.location.href);

  useEffect(() => {
    let cancelled = false;
    const inspect = async () => {
      if (!token) {
        setError('This invitation link is incomplete. Ask an administrator to resend it.');
        setLoading(false);
        return;
      }
      try {
        const response = await callBackend('api/auth/invitations/inspect', 'POST', { token });
        if (!cancelled) setInvitation(response?.invitation || null);
      } catch (inspectError) {
        if (!cancelled) setError(backendInvitationMessage(inspectError));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    inspect();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const acceptForCurrentUser = useCallback(async (currentUser) => {
    if (!currentUser?.uid || !invitation || acceptanceUidRef.current === currentUser.uid) return;
    if (!multiFactor(currentUser).enrolledFactors?.length) return;
    acceptanceUidRef.current = currentUser.uid;
    setPendingMethod('accept');
    setError('');
    try {
      await callBackend('api/auth/invitations/accept', 'POST', { token });
      sessionStorage.removeItem(INVITATION_TOKEN_STORAGE_KEY);
      onAccepted?.();
    } catch (acceptError) {
      acceptanceUidRef.current = '';
      // The token already passed inspection, so "not available" here almost
      // always means the signed-in account is not the invited address.
      const wrongAccount = acceptError?.code === 'INVITATION_NOT_AVAILABLE'
        && currentUser.email
        && invitation.email_hint;
      setError(wrongAccount
        ? `You are signed in as ${currentUser.email}, but this invitation was sent to ${invitation.email_hint}. `
          + 'Choose Use a different account, then sign in with the invited address.'
        : backendInvitationMessage(acceptError));
    } finally {
      setPendingMethod('');
    }
  }, [invitation, onAccepted, token]);

  useEffect(() => {
    if (user && invitation && !mfaResolver && sessionConfirmed) {
      acceptForCurrentUser(user);
    }
  }, [acceptForCurrentUser, invitation, mfaResolver, sessionConfirmed, user]);

  const handleFederatedSignIn = async (method) => {
    if (pendingMethod) return;
    setPendingMethod(method);
    setError('');
    try {
      const provider = method === 'google'
        ? createGoogleAuthProvider()
        : createMicrosoftAuthProvider();
      if (method === 'google') {
        // The invitee may be sharing a browser with the admin who sent it.
        provider.setCustomParameters({ prompt: 'select_account' });
      }
      const result = await signInWithPopup(auth, provider);
      setConfirmedUid(result.user?.uid || '');
      await acceptForCurrentUser(result.user);
    } catch (signInError) {
      if (signInError?.code === 'auth/multi-factor-auth-required') {
        setMfaResolver(getMultiFactorResolver(auth, signInError));
      } else {
        setError(describeAuthError(signInError));
      }
    } finally {
      setPendingMethod('');
    }
  };

  const handleEmailLinkSignIn = async (event) => {
    event.preventDefault();
    if (pendingMethod || !emailLinkAvailable) return;
    setPendingMethod('email');
    setError('');
    try {
      const result = await signInWithEmailLink(auth, email.trim(), window.location.href);
      setConfirmedUid(result.user?.uid || '');
      await acceptForCurrentUser(result.user);
    } catch (signInError) {
      if (signInError?.code === 'auth/multi-factor-auth-required') {
        setMfaResolver(getMultiFactorResolver(auth, signInError));
      } else {
        setError(describeAuthError(signInError));
      }
    } finally {
      setPendingMethod('');
    }
  };

  const handleUseDifferentAccount = async () => {
    setError('');
    acceptanceUidRef.current = '';
    await signOut(auth);
  };

  if (mfaResolver) {
    return (
      <AuthCardShell title="Join Alphamizer">
        <MFASecondFactor
          resolver={mfaResolver}
          onSuccess={() => {
            setConfirmedUid(auth.currentUser?.uid || '');
            setMfaResolver(null);
          }}
        />
      </AuthCardShell>
    );
  }

  if (user && invitation && sessionConfirmed && !multiFactor(user).enrolledFactors?.length) {
    return (
      <MFAEnrollment />
    );
  }

  return (
    <AuthCardShell
      title="Join Alphamizer"
      subtitle={invitation
        ? `${invitation.name}, choose how you would like to sign in. Your invitation was sent to ${invitation.email_hint}.`
        : 'Checking your invitation…'}
    >
        {error && <Alert severity="error">{error}</Alert>}
        {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress /></Box>}

        {!loading && invitation && (!user || !sessionConfirmed) && (
          <>
            {user && (
              <>
                <Alert severity="info">
                  {`This browser is signed in as ${user.email || 'another account'}. `
                    + `Your invitation was sent to ${invitation.email_hint}. `
                    + 'Signing in below switches this browser to the invited account.'}
                </Alert>
                <Button
                  variant="text"
                  disabled={Boolean(pendingMethod)}
                  onClick={() => setConfirmedUid(user.uid)}
                >
                  {`Continue as ${user.email || 'this account'}`}
                </Button>
              </>
            )}
            <Button
              variant="contained"
              size="large"
              disabled={Boolean(pendingMethod)}
              onClick={() => handleFederatedSignIn('google')}
            >
              {pendingMethod === 'google' ? 'Opening Google…' : 'Continue with Google'}
            </Button>
            <Button
              variant="contained"
              size="large"
              disabled={Boolean(pendingMethod)}
              onClick={() => handleFederatedSignIn('microsoft')}
            >
              {pendingMethod === 'microsoft' ? 'Opening Microsoft…' : 'Continue with Microsoft'}
            </Button>

            {emailLinkAvailable && (
              <>
                <Divider>or use the secure email link</Divider>
                <Box component="form" onSubmit={handleEmailLinkSignIn} sx={{ display: 'grid', gap: 1.5 }}>
                  <TextField
                    label="Invited email address"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    disabled={Boolean(pendingMethod)}
                    required
                  />
                  <Button type="submit" variant="outlined" disabled={Boolean(pendingMethod)}>
                    {pendingMethod === 'email' ? 'Verifying…' : 'Continue securely by email'}
                  </Button>
                </Box>
              </>
            )}
          </>
        )}

        {!loading && invitation && user && sessionConfirmed && (
          <Box sx={{ display: 'grid', gap: 1.5 }}>
            <Alert severity="info">
              Completing the invitation for {user.email || 'the selected account'}…
            </Alert>
            <Button variant="text" disabled={Boolean(pendingMethod)} onClick={handleUseDifferentAccount}>
              Use a different account
            </Button>
          </Box>
        )}
    </AuthCardShell>
  );
}
